import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Alert, Card, CardContent, Grid, Icon, IconButton } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { chunk, padStart, range, round, zip } from 'lodash'
import Input from 'components/forms/Input'
import Button from 'components/forms/Button'
import ColorPicker from 'components/forms/ColorPicker'
import Checkbox from 'components/forms/Checkbox'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import Line from 'components/echart/series/Line'
import { getMonthThreshold2 } from 'components/echart/EChartUtils'
import { getMonthList } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { getThresholdColorHtml } from 'utils/ColorUtil'
import { getUser } from 'utils/SettingUtils'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import StationAction from 'station/actions/StationAction'
import { THRESHOLD_CATEGORIES, THRESHOLD_CATEGORIES_SELECTION } from 'station/constants/StationConstants'
import {
    HIDDEN_THRESHOLDS_PLUVIOMETRIC_FOLLOW,
    THRESHOLDS_TAB,
} from '../constants/PluvioChartTypes'
import { CardTitle } from 'components/card/NewCard'

const getMonthKey = (m) => `month${padStart(m, 2, '0')}`

const matchesTypeId = (t, typeId) => !hasValue(typeId) || !hasValue(t.dataType) || parseInt(t.dataType) === typeId

const decorate = (raw, hiddenThresholds) => raw.map(th => {
    const isMonth = range(1, 13).some(m => hasValue(th[getMonthKey(m)]))
    const hidden = hiddenThresholds.some(hTh => hTh.id === th.id && hTh.code === th.code)
    return isMonth ? { ...th, value: null, isMonth, hidden } : { ...th, hidden }
})

const PluvioSuiviThresholdsTab = ({
    id,
    changeParent,
    time,
    minDate,
    maxDate,
    unit,
    tab,
    typeId,
}) => {
    const dispatch = useDispatch()
    const isConsultant = getUser().consultant === '1'

    const { allThresholds } = useSelector(state => ({
        allThresholds: state.PluviometryReducer.pluviometerThresholds,
    }), shallowEqual)

    const [tmpThresholds, setTmpThresholds] = useState([])
    const [thresholds, setThresholds] = useState([])
    const [hiddenThresholds, setHiddenThresholds] = useLocalStorage(HIDDEN_THRESHOLDS_PLUVIOMETRIC_FOLLOW, [])
    const [readyApply, setReadyApply] = useState(false)

    useEffect(() => {
        dispatch(PluviometryAction.fetchPluviometerThresholds(id))
    }, [dispatch, id])

    const baseThresholds = useMemo(
        () => decorate((allThresholds ?? []).filter(t => matchesTypeId(t, typeId)), hiddenThresholds),
        [allThresholds, typeId, hiddenThresholds],
    )

    useEffect(() => {
        setTmpThresholds(baseThresholds)
        setThresholds(baseThresholds.filter(t => !t.hidden))
        setReadyApply(false)
    }, [baseThresholds])

    const change = (idx, changes) => {
        const newThresholds = tmpThresholds.slice()
        newThresholds[idx] = { ...newThresholds[idx], ...changes }
        setTmpThresholds(newThresholds)
        if (!readyApply) setReadyApply(true)
    }

    const changeIsMonth = (idx, threshold, v) => {
        if (v) {
            change(idx, { value: null, isMonth: true, month01: threshold.value || 1 })
        } else {
            const nullMonth = range(1, 13).reduce((acc, m) => ({ ...acc, [getMonthKey(m)]: null }), {})
            change(idx, { value: threshold.month01, ...nullMonth, isMonth: false })
        }
    }

    const saveChanges = () => {
        const editedForCurrentType = tmpThresholds.map(th => ({
            ...th,
            category: th.category || THRESHOLD_CATEGORIES.SITUATION,
            dataType: th.dataType ?? (hasValue(typeId) ? String(typeId) : undefined),
        }))
        const untouchedForOtherTypes = (allThresholds ?? []).filter(t => !matchesTypeId(t, typeId))
        const merged = [...untouchedForOtherTypes, ...editedForCurrentType]
        dispatch(StationAction.updateThresholds(id, merged, 'pluviometry'))
            .then(() => dispatch(PluviometryAction.fetchPluviometerThresholds(id)))
        setReadyApply(false)
    }

    const onCancel = () => {
        setTmpThresholds(baseThresholds)
        setThresholds(baseThresholds.filter(t => !t.hidden))
        setReadyApply(false)
    }

    useEffect(() => {
        const thresholdLines = thresholds
            .filter(t => matchesTypeId(t, typeId) && t.category !== THRESHOLD_CATEGORIES.CONTROL)
            .map(t => {
                if (t.isMonth) {
                    return getMonthThreshold2(t, minDate, maxDate)
                }
                const thresholdColor = getThresholdColorHtml(t)
                return Line({
                    name: '',
                    data: [],
                    connectNulls: false,
                    markLine: {
                        silent: false,
                        symbol: 'none',
                        data: [{
                            yAxis: t.value,
                            lineStyle: {
                                color: thresholdColor,
                            },
                            label: {
                                show: true,
                                position: 'middle',
                                color: thresholdColor,
                                formatter: () => `${t.name ? `${t.name} : ${round(t.value, 3)}` : ''} ${unit ?? ''}`,
                            },
                        }],
                    },
                })
            })
        changeParent(thresholdLines)
    }, [thresholds, time, minDate, maxDate, typeId, unit])

    const isThresholdsCorrect = useMemo(() => !tmpThresholds.some(threshold => !threshold.name), [tmpThresholds])

    const allHidden = tmpThresholds.length > 0 && tmpThresholds.every(t => t.hidden)

    if (tab !== THRESHOLDS_TAB) {
        return null
    }

    return (
        <div>
            <Card className='margin-top-1'>
                <CardTitle title={i18n.thresholds} />
            </Card>
            {tmpThresholds.map((threshold, idx) => {
                const months = range(1, 13).map(m => {
                    const monthKey = getMonthKey(m)
                    return (
                        <Grid size={3} key={`${threshold.id}-${m}`}>
                            <NumberField
                                title={getMonthList()[m - 1].name}
                                value={threshold[monthKey]}
                                onChange={v => change(idx, { [monthKey]: v })}
                                disabled={!threshold.isMonth}
                                floatValue
                            />
                        </Grid>
                    )
                })
                const groups = chunk(months, 3)
                const values = Reflect.apply(zip, {}, groups)
                return (
                    <Grid container justifyContent='center' alignItems='center' spacing={3} className='margin-top-1' key={`threshold_${threshold.id ?? idx}`}>
                        <Card sx={{ width: '100%' }} elevation={10}>
                            <CardContent elevation={10}>
                                <Grid container alignItems='center' size={12} spacing={1}>
                                    {!isConsultant && (
                                        <Grid size={1} container direction='column' alignItems='center' spacing={1} sx={{ pr: 1 }}>
                                            <Grid>
                                                <IconButton
                                                    tooltip={i18n.delete}
                                                    size='custom'
                                                    sx={{
                                                        height: '25px',
                                                        width: '25px',
                                                        color: 'white',
                                                        backgroundColor: 'red',
                                                    }}
                                                    onClick={() => {
                                                        setTmpThresholds(tmpThresholds.filter((_, idx2) => idx !== idx2))
                                                        setReadyApply(true)
                                                    }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Grid>
                                            <IconButton
                                                tooltip={i18n.toggle}
                                                size='custom'
                                                sx={{
                                                    height: '25px',
                                                    width: '25px',
                                                    color: 'white',
                                                    backgroundColor: threshold.hidden ? 'rgba(53, 96, 159, 1)' : 'grey',
                                                }}
                                                onClick={() => {
                                                    const updatedTmp = tmpThresholds.map((item, i) => {
                                                        if (i === idx) {
                                                            if (hiddenThresholds.some(hTh => hTh.id === item.id && hTh.code === item.code)) {
                                                                setHiddenThresholds(prev => prev.filter(hTh => !(hTh.id === item.id && hTh.code === item.code)))
                                                            } else {
                                                                setHiddenThresholds(prev => ([...prev, { id: item.id, code: item.code }]))
                                                            }
                                                            return { ...item, hidden: !item.hidden }
                                                        }
                                                        return item
                                                    })
                                                    setTmpThresholds(updatedTmp)
                                                    setThresholds(updatedTmp.filter(t => !t.hidden))
                                                    setReadyApply(true)
                                                }}
                                            >
                                                <Icon style={{ fontSize: 15 }}>{threshold.hidden ? 'visibility' : 'visibility_off'}</Icon>
                                            </IconButton>
                                        </Grid>
                                    )}
                                    <Grid container alignItems='center' size={isConsultant ? 12 : 11}>
                                        <Grid size={7}>
                                            <Input
                                                title={i18n.name}
                                                value={threshold.name}
                                                onChange={v => change(idx, { name: v })}
                                                obligatory
                                            />
                                        </Grid>
                                        <Grid size={5}>
                                            <Select
                                                label={i18n.category}
                                                options={THRESHOLD_CATEGORIES_SELECTION}
                                                value={threshold.category || THRESHOLD_CATEGORIES.SITUATION}
                                                noNullValue
                                                keyValue='code'
                                                keyLabel='name'
                                                onChange={v => change(idx, { category: v })}
                                                obligatory
                                                noSort
                                            />
                                        </Grid>
                                        <Grid container size={12} alignItems='center' spacing={1}>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={i18n.valueLabel}
                                                    value={threshold.value}
                                                    onChange={v => change(idx, { value: v })}
                                                    disabled={threshold.isMonth}
                                                    floatValue
                                                />
                                            </Grid>
                                            <Grid size={4}>
                                                <Checkbox
                                                    checked={threshold.isOverrunThreshold === '1'}
                                                    label={i18n.thresholdOverrun}
                                                    onChange={v => change(idx, { isOverrunThreshold: v ? '1' : '0' })}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <Checkbox
                                                    checked={threshold.isMonth}
                                                    label={i18n.isMonthThreshold}
                                                    onChange={v => changeIsMonth(idx, threshold, v)}
                                                />
                                            </Grid>
                                            <Grid size={2}>
                                                <ColorPicker
                                                    label={i18n.color}
                                                    value={getThresholdColorHtml(threshold)}
                                                    onChange={v => change(idx, { htmlColor: v, color: null })}
                                                />
                                            </Grid>
                                        </Grid>
                                        {!threshold.name && (
                                            <Grid size={12}>
                                                <Alert severity='warning' sx={{ fontSize: '1rem', lineHeight: 'inherit' }}>{i18n.pleaseFillInAllTheRequiredFields}</Alert>
                                            </Grid>
                                        )}
                                        {threshold.isMonth && (
                                            <>
                                                <Grid container justifyContent='center' alignItems='center' spacing={1}>{values[0]}</Grid>
                                                <Grid container justifyContent='center' alignItems='center' spacing={1}>{values[1]}</Grid>
                                                <Grid container justifyContent='center' alignItems='center' spacing={1}>{values[2]}</Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )
            })}
            <Grid container justifyContent='center' alignItems='center' spacing={1} className='padding-bottom-1 margin-top-1 center-align'>
                <Button
                    tooltip={i18n.add}
                    onClick={() => {
                        setTmpThresholds([...tmpThresholds, { dataType: hasValue(typeId) ? String(typeId) : undefined }])
                        setReadyApply(true)
                    }}
                    icon='note_add'
                    className='green btn-floating btn-large margin-left-2'
                />
                <Button
                    tooltip={i18n.cancel}
                    onClick={onCancel}
                    icon='cancel'
                    className='red btn-floating btn-large margin-left-2 margin-right-2'
                    disabled={!readyApply}
                />
                <Button
                    tooltip={allHidden ? i18n.unhideAllThresholds : i18n.hideAllThresholds}
                    onClick={() => {
                        const hiding = !allHidden
                        const newTmp = tmpThresholds.map(t => ({ ...t, hidden: hiding }))
                        setTmpThresholds(newTmp)
                        setThresholds(hiding ? [] : newTmp)
                        setReadyApply(true)
                    }}
                    icon={allHidden ? 'visibility' : 'visibility_off'}
                    className='btn-floating btn-large margin-left-2 margin-right-2'
                />
                <Button
                    tooltip={i18n.save}
                    onClick={saveChanges}
                    icon='save'
                    className={`btn-floating btn-large ${isThresholdsCorrect && readyApply ? 'pulse' : ''}`}
                    disabled={!isThresholdsCorrect || !readyApply}
                />
            </Grid>
        </div>
    )
}

PluvioSuiviThresholdsTab.propTypes = {
    tab: PropTypes.string,
    id: PropTypes.number,
    unit: PropTypes.string,
    changeParent: PropTypes.func,
    time: PropTypes.string,
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    typeId: PropTypes.number,
}

export default PluvioSuiviThresholdsTab
